// Signed-in user and dictionaries: /me is fetched once per app load through TanStack Query; the
// routes read it from the router context. Dictionaries come from REST with a Dexie fallback offline.
import { useQuery } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import type { DictionarySummary, UserRow } from '@solungo/core/schema';
import { db } from '@/db/db';
import { dictionariesQuery, meQuery, queryClient } from './queries';
import { ApiRequestError, notifyUnauthorized } from '@/api/client';
import { storage } from '@/lib/storage';

// The user from the local copy: the last /me response (put into the query cache, so the UI locale
// and settings see the real user), or a stand-in for a device that has dictionaries but no stored
// user (older builds; not cached). Null when this device holds nothing.
async function localUser(): Promise<UserRow | null> {
  const stored = storage.getUser();
  if (stored) {
    queryClient.setQueryData(meQuery.queryKey, { user: stored });
    return stored;
  }
  const hasLocal = (await db.dictionaries.count()) > 0;
  if (hasLocal) return { id: 'offline', email: '', name: 'offline', locale: '', nativeLang: '', createdAt: '' };
  return null;
}

// The session probe behind an already-open app: a fresh row replaces the local copy, a 401 ends
// the session the same way as on any other request, a failure keeps the copy.
let probe: Promise<void> | null = null;
function probeSession(): Promise<void> {
  probe ??= queryClient
    .fetchQuery({ ...meQuery, staleTime: 0 })
    .then((res) => storage.setUser(res.user))
    .catch((e: unknown) => {
      if (e instanceof ApiRequestError && e.status === 401) notifyUnauthorized();
    })
    .finally(() => {
      probe = null;
    });
  return probe;
}

export async function loadUser(): Promise<UserRow | null> {
  const cached = queryClient.getQueryData(meQuery.queryKey);
  if (cached) return cached.user;
  // The remembered user opens the app at once, online too: a document loaded on a card (a reload,
  // iOS restoring a suspended PWA) must not wait for the network before it can paint. Online the
  // session is probed behind it; without a network there is no probe at all (on iOS a cold start
  // in airplane mode can hang for seconds).
  const stored = storage.getUser();
  if (stored) {
    queryClient.setQueryData(meQuery.queryKey, { user: stored });
    if (navigator.onLine) void probeSession();
    return stored;
  }
  if (!navigator.onLine) {
    const local = await localUser();
    if (local) return local;
  }
  try {
    const res = await queryClient.ensureQueryData(meQuery);
    storage.setUser(res.user);
    return res.user;
  } catch (e) {
    if (e instanceof ApiRequestError && e.status === 401) return null;
    if (!(e instanceof ApiRequestError && e.isNetwork)) throw e;
    // The API is unreachable: treat as signed in when the device holds a local copy
    const local = await localUser();
    if (!local) throw e;
    return local;
  }
}

export function setUser(user: UserRow): void {
  queryClient.setQueryData(meQuery.queryKey, { user });
  storage.setUser(user);
}

export function useUser(): UserRow | null {
  const q = useQuery({ ...meQuery, enabled: false });
  return q.data?.user ?? null;
}

export function useDictionaries(): DictionarySummary[] {
  const q = useQuery(dictionariesQuery);
  const local = useLiveQuery(() => db.dictionaries.toArray(), [], undefined);
  return q.data ?? local ?? [];
}

export function useDictionary(id: string): DictionarySummary | undefined {
  return useDictionaries().find((d) => d.id === id);
}

export async function refreshDictionaries(): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: dictionariesQuery.queryKey });
}
